import { create } from "zustand";
import { supabase } from "../lib/supabase";

export interface SavedListWithDetails {
  id: string;
  user_id: string;
  list_id: string;
  notes: string | null;
  created_at: string | null;
  list: {
    id: string;
    title: string;
    description: string | null;
    privacy_level: string | null;
    user_id: string;
    profiles: {
      username: string;
    };
  };
}

interface SavedListState {
  savedListIds: Set<string>;
  savedLists: SavedListWithDetails[];
  loading: boolean;
  error: string | null;

  fetchSavedListIds: (userId: string) => Promise<void>;
  fetchSavedListsWithDetails: (userId: string) => Promise<void>;
  saveList: (listId: string) => Promise<void>;
  unsaveList: (listId: string) => Promise<void>;
  isListSaved: (listId: string) => boolean;
  clearError: () => void;
}

export const useSavedListStore = create<SavedListState>((set, get) => ({
  savedListIds: new Set<string>(),
  savedLists: [],
  loading: false,
  error: null,

  fetchSavedListIds: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("saved_lists")
        .select("list_id")
        .eq("user_id", userId);

      if (error) throw error;

      const ids = new Set(data.map((s: { list_id: string }) => s.list_id));
      set({ savedListIds: ids, loading: false });
    } catch (error) {
      const message = "Failed to fetch saved lists";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  fetchSavedListsWithDetails: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("saved_lists")
        .select(
          "*, list:lists(id, title, description, privacy_level, user_id, profiles(username))",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter out entries where the list is no longer accessible (deleted or RLS-blocked)
      const validSaves = data.filter(
        (s: SavedListWithDetails) => s.list !== null,
      );
      const ids = new Set(validSaves.map((s: SavedListWithDetails) => s.list_id));
      set({ savedLists: validSaves, savedListIds: ids, loading: false });
    } catch (error) {
      const message = "Failed to fetch saved lists";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  saveList: async (listId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      set({ error: "Must be logged in to save lists" });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase.from("saved_lists").insert({
        user_id: user.id,
        list_id: listId,
      });

      if (error) throw error;

      const newIds = new Set(get().savedListIds);
      newIds.add(listId);
      set({ savedListIds: newIds, loading: false });
    } catch (error) {
      const message = "Failed to save list";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  unsaveList: async (listId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      set({ error: "Must be logged in to unsave lists" });
      return;
    }

    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from("saved_lists")
        .delete()
        .eq("user_id", user.id)
        .eq("list_id", listId);

      if (error) throw error;

      const newIds = new Set(get().savedListIds);
      newIds.delete(listId);
      const newSavedLists = get().savedLists.filter(
        (s) => s.list_id !== listId,
      );
      set({ savedListIds: newIds, savedLists: newSavedLists, loading: false });
    } catch (error) {
      const message = "Failed to unsave list";
      console.error(message, error);
      set({ error: message, loading: false });
    }
  },

  isListSaved: (listId: string) => {
    return get().savedListIds.has(listId);
  },

  clearError: () => set({ error: null }),
}));
